package diana.soleil.design.iraniancanadienbazzar.feature.profile

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.EventRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.FavoritesController
import diana.soleil.design.iraniancanadienbazzar.core.data.NewsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.SavedNewsController
import diana.soleil.design.iraniancanadienbazzar.core.data.isUpcomingAt
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.NewsArticle
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapLatest
import kotlinx.coroutines.flow.stateIn
import kotlin.time.Clock

/** Which collection the Saved screen is showing. */
enum class SavedTab { Businesses, Events, News }

/**
 * Everything the signed-in user has kept.
 *
 * [upcomingEvents] / [pastEvents] are split rather than one list: an RSVP is a commitment with a
 * date, so "what am I going to" and "what did I go to" are different questions and mixing them
 * buries the useful one.
 */
data class SavedUiState(
    val loading: Boolean = true,
    val businesses: List<Business> = emptyList(),
    val upcomingEvents: List<Event> = emptyList(),
    val pastEvents: List<Event> = emptyList(),
    val news: List<NewsArticle> = emptyList(),
) {
    val eventCount: Int get() = upcomingEvents.size + pastEvents.size
    val totalCount: Int get() = businesses.size + eventCount + news.size
}

/**
 * State holder for the Saved screen — the one place that answers "where did the things I kept go?".
 *
 * Saved businesses, RSVP'd events and bookmarked news each live in a different store (device-local
 * favorites, `users/{uid}/rsvps`, device-local news bookmarks mirrored to the account), and until now
 * only businesses had anywhere to surface. Each collection resolves ids → models independently and
 * degrades to empty on failure, so one unreachable article can't blank the whole screen.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class SavedViewModel(
    private val favoritesController: FavoritesController,
    private val savedNewsController: SavedNewsController,
    private val businessRepository: BusinessRepository,
    private val newsRepository: NewsRepository,
    private val eventRepository: EventRepository,
) : ViewModel() {

    private val _tab = MutableStateFlow(SavedTab.Businesses)
    val tab: StateFlow<SavedTab> = _tab

    fun selectTab(tab: SavedTab) { _tab.value = tab }

    /** Unsave from the heart on a saved business card; the list updates live off the favorites flow. */
    fun toggleFavorite(businessId: String) = favoritesController.toggle(businessId)

    /** Remove an article from the reading list; the list updates live off the saved-news flow. */
    fun toggleSavedNews(articleId: String) = savedNewsController.toggle(articleId)

    private val businesses = favoritesController.favorites
        .mapLatest { ids -> ids.mapNotNull { (businessRepository.getBusiness(it) as? DataResult.Success)?.data } }
        .catch { emit(emptyList()) }

    private val news = savedNewsController.saved
        .mapLatest { ids ->
            ids.mapNotNull { (newsRepository.getArticle(it) as? DataResult.Success)?.data }
                .sortedByDescending { it.publishedAtMillis }
        }
        .catch { emit(emptyList()) }

    // RSVPs are ids only, so each one is a document read. Sorted soonest-first for upcoming and
    // most-recent-first for past — the nearest commitment and the latest memory both sit at the top.
    private val events = eventRepository.observeMyRsvps()
        .mapLatest { ids -> ids.mapNotNull { (eventRepository.getEvent(it) as? DataResult.Success)?.data } }
        .catch { emit(emptyList()) }

    val state: StateFlow<SavedUiState> =
        combine(businesses, events, news) { saved, rsvps, articles ->
            val now = Clock.System.now().toEpochMilliseconds()
            val (upcoming, past) = rsvps.partition { it.isUpcomingAt(now) }
            SavedUiState(
                loading = false,
                businesses = saved,
                upcomingEvents = upcoming.sortedBy { it.startsAtMillis },
                pastEvents = past.sortedByDescending { it.startsAtMillis },
                news = articles,
            )
        }
            .catch { emit(SavedUiState(loading = false)) }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), SavedUiState())

    /** Saved ids, so the business cards can render their filled hearts without a second source. */
    val favorites: StateFlow<Set<String>> = favoritesController.favorites
        .map { it }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptySet())
}
